import React, { useState, useRef } from "react" 
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks"
import { Dropdown } from "../Dropdown" 
import { toggleShowSecondaryModal, setSecondaryModalProps, setSecondaryModalType } from "../../slices/secondaryModalSlice" 
import { Toast, Ticket, Status, UserProfile } from "../../types/common"
import { CgProfile } from "react-icons/cg"
import { Avatar } from "../page-elements/Avatar"
import { IconEye } from "../icons/IconEye";
import { IconEyeSlash } from "../icons/IconEyeSlash";
import { 
	useAddTicketAssigneeMutation,
	useDeleteTicketAssigneeMutation, 
} from "../../services/private/ticket"
import { useAddNotificationMutation } from "../../services/private/notification"
import { addToast } from "../../slices/toastSlice"
import { v4 as uuidv4 } from "uuid"
import { getUserInitials, displayUser } from "../../helpers/functions"
import { FiPlus as PlusIcon } from "react-icons/fi";
import { IconPlus } from "../icons/IconPlus"
import { TICKETS } from "../../helpers/routes"
import { LoadingSpinner } from "../LoadingSpinner"
import { STANDARD_DROPDOWN_ITEM, STANDARD_DROPDOWN_ITEM_NO_HOVER } from "../../helpers/constants"

type Props = {
	closeDropdown: () => void
	ticket: Ticket | null | undefined
	ticketAssignee: UserProfile | null | undefined
	ticketWatchers: Array<UserProfile> | null | undefined
	isMobile?: boolean
}

export const WatchMenuDropdown = React.forwardRef<HTMLDivElement, Props>(({isMobile, closeDropdown, ticket, ticketAssignee, ticketWatchers}: Props, ref) => {
	const { userProfile } = useAppSelector((state) => state.userProfile)
	const { userRoleLookup } = useAppSelector((state) => state.userRole)
	const { ticketTypes } = useAppSelector((state) => state.ticketType)
	const { notificationTypes } = useAppSelector((state) => state.notificationType)
	const userRole = userProfile && userRoleLookup ? userRoleLookup[userProfile?.userRoleId] : null
	const dispatch = useAppDispatch()
	const isAdminOrBoardAdmin = userRole && (userRole === "ADMIN" || userRole === "BOARD_ADMIN")
	const isTicketReporter = userRole && userRole === "USER" && ticket?.userId === userProfile?.id
	const watchNotificationType = notificationTypes.find((n) => n.name === "Watching Ticket")
	const epicTicketType = ticketTypes.find((ticketType) => ticketType.name === "Epic")
	const watcher: UserProfile | undefined = ticketWatchers?.find((watcher: UserProfile) => watcher.id === userProfile?.id)
	const [ addTicketAssignee, {isLoading: addTicketAssigneeLoading} ] = useAddTicketAssigneeMutation()
	const [ deleteTicketAssignee, {isLoading: isDeleteTicketAssigneeLoading}] = useDeleteTicketAssigneeMutation()
	const [ addNotification, {isLoading: isAddNotificationLoading}] = useAddNotificationMutation()
	const [ submitLoading, setSubmitLoading ] = useState(false)

	const addTicketWatcher = async (ticketId: number | null | undefined, userId: number | null | undefined) => {
		let defaultToast: Toast = {
			id: uuidv4(),
			type: "success",
			animationType: "animation-in",
			message: "You are now watching this ticket!"
		}
		setSubmitLoading(true)
		try {
			if (watchNotificationType && userProfile && ticketId && userId){
				await addTicketAssignee({ticketId: ticketId, userIds: [userId], isWatcher: true}).unwrap()
				await addNotification({
					recipientId: userId,
					senderId: userProfile.id,
					ticketId: ticketId,
					objectLink: `${TICKETS}/${ticketId}`,
					notificationTypeId: watchNotificationType.id,
				}).unwrap()
				dispatch(addToast(defaultToast))
			}
		}
		catch (e) {
			dispatch(addToast({
				...defaultToast,
				type: "failure",
				message: "Failed to add ticket watcher"
			}))
		}
		finally {
			setSubmitLoading(false)
		}
	}

	const showAddWatchersModal = () => {
		dispatch(toggleShowSecondaryModal(true))
		dispatch(setSecondaryModalProps({ticketAssigneeId: ticketAssignee?.id, ticketId: ticket?.id}))
		dispatch(setSecondaryModalType("ADD_TICKET_WATCHERS_MODAL"))
	}

	const deleteTicketWatcher = async (ticketId: number | null | undefined, userId: number | null | undefined) => {
		let defaultToast: Toast = {
			id: uuidv4(),
			type: "success",
			animationType: "animation-in",
			message: "Ticket watcher removed successfully!"
		}
		setSubmitLoading(true)
		try {
			if (ticketId && userId){
				await deleteTicketAssignee({ticketId: ticketId, userId: userId, isWatcher: true}).unwrap()
				dispatch(addToast(defaultToast))
			}
		}
		catch (e) {
			dispatch(addToast({
				...defaultToast,
				type: "failure",
				message: "Failed to remove ticket watcher"
			}))
		}
		finally {
			setSubmitLoading(false)
		}
	} 

	let options = {
		...(userProfile?.id !== ticketAssignee?.id ? (!watcher ? {
			"Start Watching": () => {
				addTicketWatcher(ticket?.id, userProfile?.id)
			}
		} : {
			"Stop Watching": () => {
				deleteTicketWatcher(ticket?.id, userProfile?.id)
			}
		}) : {}),
		...(isAdminOrBoardAdmin || isTicketReporter ? {
			"Add Watchers": () => {
				showAddWatchersModal()
				closeDropdown()	
			}
		} : {}),
	}
	return (
		<Dropdown isMobile = {isMobile} ref = {ref}>
			<ul>
				{Object.keys(options).map((option) => {
					if (option === "Start Watching" || option === "Stop Watching"){
						return (
							<li
								key={option}
								onClick={(e) => {
									if (e.defaultPrevented){
										return
									}
									options[option as keyof typeof options]?.()
								}}
								className={`${STANDARD_DROPDOWN_ITEM}`}
								role="menuitem"
							>
								<div className = "tw-flex tw-flex-row tw-justify-between tw-items-center tw-gap-x-2">
									{option === "Start Watching" ? 
									<div className = "tw-flex tw-flex-row tw-gap-x-2 tw-items-center"><IconEye className = "tw-w-6 tw-h-6"/><p>{option}</p></div>
									: <div className = "tw-flex tw-flex-row tw-gap-x-2 tw-items-center"><IconEyeSlash className = "tw-w-6 tw-h-6"/><p>{option}</p></div>}
									{submitLoading ? <LoadingSpinner/> : null}
								</div>
							</li>
						)
					}
				})}
				<li
					className={STANDARD_DROPDOWN_ITEM_NO_HOVER}
					role = "menuitem"
				>
					<div className = "tw-flex tw-flex-col tw-gap-y-2 tw-py-1">
						<p>Watching This Issue</p>
						<div className = "tw-flex tw-flex-col tw-gap-y-2">
							{
								ticketWatchers?.map((watcher) => {
									return (
										<div className = "tw-flex tw-flex-row tw-items-center tw-gap-x-2 tw-py-1">
											<Avatar userInitials={getUserInitials(watcher)} imageUrl={watcher.imageUrl} className = "tw-rounded-full !tw-w-6 !tw-h-6"/>
											<p key = {`watcher_${watcher.id}`}>{displayUser(watcher)}
											</p>
										</div>
									)
								})
							}
						</div>
					</div>
				</li>
				{
					isAdminOrBoardAdmin || isTicketReporter ? 
					<li
						className={`${STANDARD_DROPDOWN_ITEM} tw-border-t`}
						onClick={() => options["Add Watchers"]?.()}
						role = "menuitem"
					>
						<button onClick={() => {
							options["Add Watchers"]?.()					
						}}>
							<div className = "tw-flex tw-flex-row tw-items-center tw-gap-x-2">
								<IconPlus className = "tw-w-6 tw-h-6"/>
								<p>Add Watchers</p>
							</div>
						</button>
					</li> : null
				}
			</ul>
		</Dropdown>
	)	
})

